import React, { useMemo } from 'react';
import Lozenge from '@atlaskit/lozenge';
import { CustomPanel, DynamicResultTable, getCurrencyConvertedValue } from '@css-xp/base';
import { emptyStateProp } from '@css-xp/ppc-common/src/empty-state-prop';
import './blended-partner-discount.scss';

interface BlendedComputation {
  newGlp: number;
  previousGlp: number;
  upsellPercentage: string;
  renewPercentage: string;
  upsellValue: number;
  renewalValue: number;
  upsellMargin: number;
  renewalMargin: number;
  blendedMargin: number;
}

interface BlendedPartnerDiscountProps {
  blendedMargin: {
    percent: string;
    blendedComputation?: BlendedComputation;
  };
  currency: string;
  shouldConvertValueBasedOnCurrency: boolean;
}

export const BlendedPartnerDiscount = ({
  blendedMargin,
  currency,
  shouldConvertValueBasedOnCurrency,
}: BlendedPartnerDiscountProps) => {
  const grossListPriceColumns = useMemo(() => {
    return {
      cells: [
        {
          key: 'Description',
          content: 'Description',
          width: 80,
        },
        {
          key: `List price (${currency})`,
          content: `List price (${currency})`,
          width: 20,
        },
      ],
    };
  }, [currency]);

  const intendedRenewalPlanColumns = useMemo(() => {
    return {
      cells: [
        {
          key: 'Plan',
          content: 'Plan',
          width: 40,
        },
        {
          key: `Amount (${currency})`,
          content: `Amount (${currency})`,
          width: 20,
        },
        {
          key: 'Percentage',
          content: 'Percentage',
          width: 20,
        },
        {
          key: `Discount (${currency})`,
          content: `Discount (${currency})`,
          width: 20,
        },
      ],
    };
  }, [currency]);

  const grossListPriceRows = useMemo(() => {
    if (blendedMargin?.blendedComputation) {
      return [
        {
          key: 'current-plan-gross-list-price',
          cells: [
            {
              content: `Current plan gross list price (${currency})`,
            },
            {
              content: shouldConvertValueBasedOnCurrency
                ? getCurrencyConvertedValue(blendedMargin.blendedComputation.previousGlp, currency)
                : blendedMargin.blendedComputation.previousGlp.toFixed(2),
            },
          ],
        },
        {
          key: 'intended-renewal-plans-gross-list-price',
          cells: [
            {
              content: `Intended renewal plans gross list price (${currency})`,
            },
            {
              content: shouldConvertValueBasedOnCurrency
                ? getCurrencyConvertedValue(blendedMargin.blendedComputation.newGlp, currency)
                : blendedMargin.blendedComputation.newGlp.toFixed(2),
            },
          ],
        },
      ];
    }
    return [];
  }, [blendedMargin.blendedComputation, currency, shouldConvertValueBasedOnCurrency]);

  const intendedRenewalPlanRows = useMemo(() => {
    if (blendedMargin?.blendedComputation) {
      return [
        {
          key: 'renewal-portion',
          cells: [
            {
              content: (
                <>
                  <div>Renewal portion</div>
                  <div className="subtext">Current plan</div>
                </>
              ),
            },
            {
              content: shouldConvertValueBasedOnCurrency
                ? getCurrencyConvertedValue(blendedMargin.blendedComputation.renewalValue, currency)
                : blendedMargin.blendedComputation.renewalValue.toFixed(2),
            },
            {
              content: <Lozenge>{blendedMargin.blendedComputation.renewPercentage}%</Lozenge>,
            },
            {
              content: shouldConvertValueBasedOnCurrency
                ? getCurrencyConvertedValue(blendedMargin.blendedComputation.renewalMargin, currency)
                : blendedMargin.blendedComputation.renewalMargin.toFixed(2),
            },
          ],
        },
        {
          key: 'upgrade-portion',
          cells: [
            {
              content: (
                <>
                  <div>Upgrade portion</div>
                  <div className="subtext">(Intended renewal plan less current plan)</div>
                </>
              ),
            },
            {
              content: shouldConvertValueBasedOnCurrency
                ? getCurrencyConvertedValue(blendedMargin.blendedComputation.upsellValue, currency)
                : blendedMargin.blendedComputation.upsellValue.toFixed(2),
            },
            {
              content: <Lozenge>{blendedMargin.blendedComputation.upsellPercentage}%</Lozenge>,
            },
            {
              content: shouldConvertValueBasedOnCurrency
                ? getCurrencyConvertedValue(blendedMargin.blendedComputation.upsellMargin, currency)
                : blendedMargin.blendedComputation.upsellMargin.toFixed(2),
            },
          ],
        },
        {
          key: 'blended-portion',
          cells: [
            {
              content: (
                <>
                  <div className="heading">Blended rate based on gross list price</div>
                  <div className="subtext">
                    <p>(Total discount / intended renewal plan)</p>
                    <p>We round up the calculated blended rate to two decimal places.</p>
                  </div>
                </>
              ),
            },
            {
              content: shouldConvertValueBasedOnCurrency
                ? getCurrencyConvertedValue(blendedMargin.blendedComputation.newGlp, currency)
                : blendedMargin.blendedComputation.newGlp.toFixed(2),
            },
            {
              content: <Lozenge>{blendedMargin.percent}%</Lozenge>,
            },
            {
              content: shouldConvertValueBasedOnCurrency
                ? getCurrencyConvertedValue(blendedMargin.blendedComputation.blendedMargin, currency)
                : blendedMargin.blendedComputation.blendedMargin.toFixed(2),
            },
          ],
        },
      ];
    }
    return [];
  }, [blendedMargin.blendedComputation, blendedMargin.percent, currency, shouldConvertValueBasedOnCurrency]);

  const containerHeader = 'Blended partner discount calculation';

  return (
    <CustomPanel
      expandedHeader={containerHeader}
      collapsedHeader={containerHeader}
      version={2}
      testId="blended-partner-discount-custom-panel"
    >
      <div className="blended-partner-discount-custom-panel">
        <div className="blended-information-text">
          <p>
            When a renewal is made at a higher plan, a blended rate is applied. This rate is calculated based on the
            gross list price (full period charges).
          </p>
          <p>
            The actual partner benefit, shown above, is calculated by applying this rate to the final charge, after
            adjustment of promotions.
          </p>
        </div>
        <div className="gross-list-price-table">
          <DynamicResultTable
            emptyStateProps={emptyStateProp}
            head={grossListPriceColumns}
            rows={grossListPriceRows}
            boxShadow={false}
            headingText="1. Gross List Price"
          />
        </div>
        <div className="distribution-of-intended-renewal-plan-table">
          <DynamicResultTable
            emptyStateProps={emptyStateProp}
            head={intendedRenewalPlanColumns}
            rows={intendedRenewalPlanRows}
            boxShadow={false}
            headingText="2. Distribution of intended renewal plan"
          />
        </div>
      </div>
    </CustomPanel>
  );
};
